import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Mail, Send, CheckCircle, XCircle, Loader2, Activity } from "lucide-react";

export default function EmailAdmin() {
  const [status, setStatus] = useState({ enabled: false, from_email: "", from_name: "" });
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);
  const [recent, setRecent] = useState([]);

  const loadRecent = () => api.get("/admin/email/recent").then(r => setRecent(r.data || [])).catch(() => {});

  useEffect(() => {
    api.get("/admin/email/status").then(r => setStatus(r.data));
    loadRecent();
  }, []);

  const sendTest = async () => {
    if (!testTo) { toast.error("Email required"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/admin/email/test", { to: testTo });
      toast.success(`Sent · id ${data.id}`);
      loadRecent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-email">
      <h2 className="text-2xl font-bold text-[#1a3c5e]">Email (Resend)</h2>

      <div className={`p-4 rounded-2xl border ${status.enabled ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-amber-50 border-amber-200 text-amber-700"}`}>
        <div className="flex items-center gap-2 font-semibold">
          {status.enabled ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
          {status.enabled ? "Resend is configured" : "Resend not configured"}
        </div>
        <div className="text-xs mt-1 opacity-80">
          From: <code className="font-mono">{status.from_name} &lt;{status.from_email}&gt;</code>
        </div>
        {!status.enabled && (
          <div className="text-xs mt-2">Set <code>RESEND_API_KEY</code> in backend/.env and restart backend.</div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-3">
        <div className="text-sm font-semibold text-[#1a3c5e] flex items-center gap-2"><Send className="w-4 h-4" /> Send Test Email</div>
        <div className="flex gap-2">
          <input type="email" value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="recipient@example.com"
            data-testid="email-test-to" className="flex-1 border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-[#F7941D]" />
          <button onClick={sendTest} disabled={sending || !status.enabled} data-testid="email-test-send"
            className="px-5 py-2 bg-[#F7941D] hover:bg-[#e0861a] disabled:opacity-50 text-white text-sm font-semibold rounded-xl inline-flex items-center gap-1">
            {sending && <Loader2 className="w-4 h-4 animate-spin" />} Send Test
          </button>
        </div>
        <p className="text-xs text-gray-500">
          Note: Resend requires the FROM domain to be verified at <a href="https://resend.com/domains" className="text-[#F7941D] underline">resend.com/domains</a>.
          Until then test emails will fail with a verification error.
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200">
        <div className="px-5 py-3 border-b border-gray-200 flex items-center gap-2 text-sm font-semibold text-[#1a3c5e]">
          <Activity className="w-4 h-4" /> Recent Email Activity
        </div>
        <div className="max-h-[400px] overflow-y-auto">
          {recent.length === 0 && <div className="text-center py-8 text-gray-500 text-sm">No email events yet.</div>}
          {recent.map((it, i) => (
            <div key={it.id || i} className="px-5 py-2 border-b border-gray-100 text-xs flex items-center gap-3" data-testid={`email-event-${i}`}>
              {it.event_type?.endsWith("sent") ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : <XCircle className="w-4 h-4 text-rose-500" />}
              <span className="font-mono text-[10px] text-gray-500 w-32">{(it.created_at || "").slice(0, 19).replace("T", " ")}</span>
              <span className="font-semibold w-32">{it.event_type}</span>
              <span className="flex-1 text-gray-600 truncate">{it.details?.to} — {it.details?.subject || it.details?.error}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
